'use client';

import { useCallback, useState } from 'react';
import { toasterApi } from '../toast/ToastProvider';
import { downloadPocScriptAsFile } from '../../utils';
import type { InternalEnrichedFinding } from '../../types';

type GeneratePocScriptApi = (params: {
  applicationId: string;
  findingId: string;
}) => Promise<void>;

type UpdateFindingPocApi = (
  findingId: string,
  data: {
    pocScript: null;
    pocScriptBlockedByGuardrails: null;
    pocScriptGuardrailsFeedback: null;
  },
) => Promise<void>;

const POC_SCRIPT_T_KEYS = [
  'pocScriptGenerationStarted',
  'pocScriptGenerationStartedDescription',
  'pocScriptGenerateError',
  'deletePocScriptButton',
  'deletePocScriptSuccessDescription',
] as const;

type PocScriptTranslationKey = (typeof POC_SCRIPT_T_KEYS)[number];

type UsePocScriptActionsParams = {
  applicationId: string | undefined;
  findingId: string | undefined;
  finding: InternalEnrichedFinding | null;
  generatePocScriptApi: GeneratePocScriptApi;
  updateFindingPocApi: UpdateFindingPocApi;
  refetchFinding: () => Promise<{ data?: InternalEnrichedFinding }>;
  invalidateQueries?: () => Promise<void>;
  t: (key: PocScriptTranslationKey) => string;
  waitForSseCompletion?: boolean;
};

type UsePocScriptActionsResult = {
  onGeneratePocScript: () => void;
  onRegeneratePocScript: () => void;
  onDownloadPocScript: () => void;
  onDeletePocScript: () => void;
  isGeneratingPocScript: boolean;
  isDeletingPocScript: boolean;
  setPocGenerationComplete: () => void;
};

export function usePocScriptActions({
  applicationId,
  findingId,
  finding,
  generatePocScriptApi,
  updateFindingPocApi,
  refetchFinding,
  invalidateQueries,
  t,
  waitForSseCompletion = false,
}: UsePocScriptActionsParams): UsePocScriptActionsResult {
  const [isGeneratingPocScript, setIsGeneratingPocScript] = useState(false);
  const [isDeletingPocScript, setIsDeletingPocScript] = useState(false);

  const setPocGenerationComplete = useCallback(() => {
    setIsGeneratingPocScript(false);
  }, []);

  const onGeneratePocScript = useCallback(() => {
    if (!applicationId || !findingId) return;
    setIsGeneratingPocScript(true);
    generatePocScriptApi({ applicationId, findingId })
      .then(() => {
        toasterApi.success({
          title: t('pocScriptGenerationStarted'),
          description: t('pocScriptGenerationStartedDescription'),
        });
      })
      .catch((error: Error) => {
        if (waitForSseCompletion) {
          setIsGeneratingPocScript(false);
        }
        toasterApi.error({
          title: t('pocScriptGenerateError'),
          description: error.message,
        });
      })
      .finally(() => {
        if (!waitForSseCompletion) {
          setIsGeneratingPocScript(false);
        }
      });
  }, [applicationId, findingId, generatePocScriptApi, t, waitForSseCompletion]);

  const onRegeneratePocScript = useCallback(() => {
    if (!findingId) return;
    setIsGeneratingPocScript(true);
    const clearPayload = {
      pocScript: null,
      pocScriptBlockedByGuardrails: null,
      pocScriptGuardrailsFeedback: null,
    };
    updateFindingPocApi(findingId, clearPayload)
      .then(() => refetchFinding())
      .then(() => invalidateQueries?.())
      .then(() => {
        if (applicationId && findingId) {
          return generatePocScriptApi({ applicationId, findingId });
        }
      })
      .then(() => {
        toasterApi.success({
          title: t('pocScriptGenerationStarted'),
          description: t('pocScriptGenerationStartedDescription'),
        });
      })
      .catch((error: Error) => {
        if (waitForSseCompletion) {
          setIsGeneratingPocScript(false);
        }
        toasterApi.error({
          title: t('pocScriptGenerateError'),
          description: error.message,
        });
      })
      .finally(() => {
        if (!waitForSseCompletion) {
          setIsGeneratingPocScript(false);
        }
      });
  }, [
    applicationId,
    findingId,
    generatePocScriptApi,
    updateFindingPocApi,
    refetchFinding,
    invalidateQueries,
    t,
    waitForSseCompletion,
  ]);

  const onDownloadPocScript = useCallback(() => {
    if (!finding?.pocScript || !finding.id) return;
    downloadPocScriptAsFile({
      pocScript: finding.pocScript,
      findingId: finding.id,
    });
  }, [finding]);

  const onDeletePocScript = useCallback(() => {
    if (!findingId) return;
    setIsDeletingPocScript(true);
    const clearPayload = {
      pocScript: null,
      pocScriptBlockedByGuardrails: null,
      pocScriptGuardrailsFeedback: null,
    };
    updateFindingPocApi(findingId, clearPayload)
      .then(() => refetchFinding())
      .then(() => invalidateQueries?.())
      .then(() => {
        toasterApi.success({
          title: t('deletePocScriptButton'),
          description: t('deletePocScriptSuccessDescription'),
        });
      })
      .catch((error: Error) => {
        toasterApi.error({
          title: t('pocScriptGenerateError'),
          description: error.message,
        });
      })
      .finally(() => {
        setIsDeletingPocScript(false);
      });
  }, [findingId, updateFindingPocApi, refetchFinding, invalidateQueries, t]);

  return {
    onGeneratePocScript,
    onRegeneratePocScript,
    onDownloadPocScript,
    onDeletePocScript,
    isGeneratingPocScript,
    isDeletingPocScript,
    setPocGenerationComplete,
  };
}
